import React, { useEffect, useRef, useState } from "react";
import { ActivityIndicator, View, Platform } from "react-native";
import { WebView } from "react-native-webview";
import Config from "react-native-config";
import base64 from "base-64";
import * as sha256 from "js-sha256";

import { useNavigation } from "@react-navigation/native";
import { useTokenManagement } from "@lib/hooks/login/useTokenManagement";
import { getRedirectUri, Logger } from "@lib/utilities/HelperFunctions";
import { POLICY_LISTING_ROUTE } from "../../navigation/NavigationConstants";
import Crashlytics from "@lib/utilities/crashlyticsUtils/CrashlyticsUtils";

const WebLoginScreen = () => {
  const navigation = useNavigation();
  const { saveTokensAndNavigate } = useTokenManagement();

  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const codeVerifierRef = useRef<string | null>(null);

  const AUTH0_DOMAIN = Config.REACT_APP_EXZEO_ID_DOMAIN;
  const CLIENT_ID = Config.REACT_APP_EXZEO_ID_CLIENT_ID;

  const getEnvironment = () => {
    if (Config.REACT_APP_ENV === "prod") {
      return "";
    } else if (Config.REACT_APP_ENV === "stage") {
      return "stage.";
    } else {
      return "demo.";
    }
  };

  useEffect(() => {
    const setupAuth = async () => {
      try {
        // TO-DO : Need to put this chars in env file for security purpose 
        const randomChars =
          "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
        const randomString = (len: number) => {
          let s = "";
          for (let i = 0; i < len; i++) {
            s += randomChars[Math.floor(Math.random() * randomChars.length)];
          }
          return s;
        };

        const codeVerifier = randomString(64);
        codeVerifierRef.current = codeVerifier;

        // sha256 the verifier and base64url-encode
        const hashed = sha256.sha256.arrayBuffer(codeVerifier);
        const b64 = base64.encode(
          String.fromCharCode(...new Uint8Array(hashed as ArrayBuffer)),
        );
        const codeChallenge = b64
          .replace(/\+/g, "-")
          .replace(/\//g, "_")
          .replace(/=+$/, "");

        const params =
          `client_id=${CLIENT_ID}&` +
          `response_type=code&` +
          `scope=${encodeURIComponent(
            "openid profile email offline_access",
          )}&` +
          `redirect_uri=${encodeURIComponent(getRedirectUri())}&` +
          `code_challenge=${encodeURIComponent(codeChallenge)}&` +
          `code_challenge_method=S256`;

        const authUrl = `https://${AUTH0_DOMAIN}/authorize?${params}`;
        setAuthUrl(authUrl);
      } catch (error) {
        Logger("Failed to setup PKCE: " + JSON.stringify(error));
      }
    };
    setupAuth();
  }, []);

  // --- Exchange Auth Code for Tokens ---
  const exchangeCodeForToken = async (code: string) => {
    try {
      setLoading(true);
      Logger("Exchanging code for token...");

      const tokenResponse = await fetch(`https://${AUTH0_DOMAIN}/oauth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grant_type: "authorization_code",
          client_id: CLIENT_ID,
          code,
          redirect_uri: getRedirectUri(),
          code_verifier: codeVerifierRef.current,
        }),
      });

      const tokens = await tokenResponse.json();

      if (tokens.error) {
        Logger("Token exchange error: " + JSON.stringify(tokens));
        setLoading(false);
        return;
      }

      await saveTokensAndNavigate(tokens.access_token, tokens.refresh_token);
      navigation.navigate(POLICY_LISTING_ROUTE as never);
    } catch (error) {
      Logger("Token exchange failed: " + JSON.stringify(error));
    } finally {
      setLoading(false);
    }
  };

  // --- Intercept redirect in WebView ---
  const onShouldStartLoadWithRequest = (request: any) => {
    const { url } = request;
    try {
      if (url && url.startsWith(getRedirectUri())) {
        Logger("Intercepted redirect URI: " + url);
        const codeMatch = url.match(/[\?&]code=([^&]+)/);
        const errorMatch = url.match(/[\?&]error=([^&]+)/);
        if (codeMatch) {
          const code = decodeURIComponent(codeMatch[1]);
          exchangeCodeForToken(code);
        } else if (errorMatch) {
          const err = decodeURIComponent(errorMatch[1]);
          Logger("Auth error in redirect: " + err);
        } else {
          Logger("No code or error found in redirect URL");
        }
        return false; // stop WebView from navigating to redirect URI
      }
    } catch (e) {
      Logger("Error handling navigation request: " + JSON.stringify(e));
    }
    return true;
  };

  useEffect(() => {
    Crashlytics.logScreen("Auth WebView");
  }, []);

  return (
    <View style={{ flex: 1, backgroundColor: "white" }}>
      {loading && (
        <ActivityIndicator
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: [{ translateX: -15 }, { translateY: -15 }],
          }}
          size="large"
        />
      )}

      {authUrl && (
        <WebView
          source={{ uri: authUrl }}
          onLoadEnd={() => setLoading(false)}
          onShouldStartLoadWithRequest={onShouldStartLoadWithRequest}
          startInLoadingState
          incognito
          cacheEnabled={false}
          onError={(syntheticEvent) => {
            const { nativeEvent } = syntheticEvent;
            Logger("WebView error: " + nativeEvent.description);
          }}
          javaScriptEnabled
        />
      )}
    </View>
  );
};

export default WebLoginScreen;
